import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Alert,
  Avatar,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { LiquidGlassButton } from '../../design-system'
import { colors, spacing, typography } from '../../design-system/tokens'
import { organizationApi, type Organization } from '../../services/organizationApi'
import { useOrgStore } from '../../store/orgStore'
import { useNavigate } from 'react-router-dom'

function getOrgInitial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?'
}

function getAvatarColor(name: string): string {
  const colors = ['#E8734A', '#5B8AF0', '#4CAF50', '#FF9800', '#9C27B0', '#00BCD4']
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

export function OrgSelector() {
  const navigate = useNavigate()
  const { setActiveOrg } = useOrgStore()
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    organizationApi.listMyOrgs()
      .then(setOrgs)
      .catch((err) => setError(err.message ?? 'Failed to load organizations'))
      .finally(() => setLoading(false))
  }, [])

  const handleSelect = (org: Organization) => {
    setActiveOrg(org)
    navigate('/workspaces', { replace: true })
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.accent.main }} />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: colors.background.page,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 6,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 640 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <img
            src="/archer_logo.svg"
            alt="Ignis"
            style={{ height: '32px', marginBottom: '24px', opacity: 0.9 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <Typography
            sx={{
              fontFamily: typography.fontFamily.display,
              fontSize: '2rem',
              fontWeight: typography.fontWeight.bold,
              color: colors.text.heading,
              mb: 1,
            }}
          >
            Select an organization
          </Typography>
          <Typography sx={{ fontSize: typography.fontSize.md, color: colors.text.secondary }}>
            You belong to multiple organizations. Choose which one to work in.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Org cards grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
          {orgs.map((org) => (
            <Card
              key={org.id}
              elevation={0}
              sx={{
                border: `1px solid ${colors.border.subtle}`,
                borderRadius: spacing.borderRadius.lg,
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  borderColor: colors.accent.main,
                  boxShadow: `0 0 0 1px ${colors.accent.main}20`,
                },
              }}
            >
              <CardActionArea onClick={() => handleSelect(org)} sx={{ p: 0 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    {org.logoUrl ? (
                      <Avatar src={org.logoUrl} sx={{ width: 40, height: 40 }} />
                    ) : (
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: getAvatarColor(org.name),
                          fontSize: '1rem',
                          fontWeight: typography.fontWeight.bold,
                        }}
                      >
                        {getOrgInitial(org.name)}
                      </Avatar>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: typography.fontWeight.semibold,
                          fontSize: typography.fontSize.md,
                          color: colors.text.heading,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {org.name}
                      </Typography>
                      {org.industry && (
                        <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.text.tertiary }}>
                          {org.industry}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {org.description && (
                    <Typography
                      sx={{
                        fontSize: typography.fontSize.sm,
                        color: colors.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {org.description}
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {/* Create new org */}
        <Box sx={{ textAlign: 'center' }}>
          <LiquidGlassButton
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate('/onboarding')}
          >
            Create new organization
          </LiquidGlassButton>
        </Box>
      </Box>
    </Box>
  )
}
